/* ----------------------------------------------------------------------------------------------------------------
   THE MOBILE MENU'S PICTURES, STARTED ONE TAP EARLY

   WHAT WAS WRONG. Every image in the header drawer is `loading="lazy"` — deliberately, and it must stay that way:
   the menu is on every page of the store and eager-loading it once cost ~95 thumbnails and 1.5 MB on every single
   page view. But a lazy image inside a `hidden` panel is not merely deprioritised, it is not requested at all, so
   the second-layer panel's banner began downloading at the instant the panel appeared. The panel fades in over
   150ms; the picture landed some way after it. That is the bump.

   WHAT THIS DOES. The drawer opens on a list of categories, and choosing one is a second tap — half a second at
   the very least, usually longer. That gap is the opening this uses:

     drawer opens   → the four panel banners are promoted to eager at high priority. Four pictures, and only for
                      a reader who has actually opened the menu, so the page-view cost stays at nothing.
     category tapped → everything else inside THAT panel is promoted, at its own low priority. Scoped to the one
                      panel because a panel holds a row of city thumbnails, and promoting all four panels' worth
                      would put thirty requests in flight to show eight of them.

   By the time the panel appears its banner is normally in cache and appears with it. For the reader who taps
   straight through, `data-cf-fade` lets theme.css ease the picture in rather than have it drop into place — and
   that attribute is written here, on the images that were NOT already complete, so nothing is ever left holding a
   picture at zero opacity if this file fails to run.

   Promoting works by assignment: setting `loading` from `lazy` to `eager` on an image that has not started
   loading begins the fetch, hidden or not. It is idempotent — a WeakSet keeps a second open from redoing the
   first one's work, and the panel hook only ever adds images the banner pass did not already take.
---------------------------------------------------------------------------------------------------------------- */
(() => {
  'use strict';

  const SIDEBAR = '.header-sidebar';
  const PANELS = '.header-sidebar__collapsible-panel';
  const BANNER = 'img.dn__banner';

  const started = new WeakSet();

  /* Hidden until it has pixels, but only if it does not have them yet: an image already in cache is complete
     before this runs, and giving that one a fade would be a flicker introduced to prevent one. */
  const easeIn = (img) => {
    if (img.complete && img.naturalWidth > 0) return;
    img.dataset.cfFade = '1';
    const show = () => {
      img.dataset.cfShown = '1';
    };
    img.addEventListener('load', show, { once: true });
    /* A picture that fails still has to stop being invisible — otherwise the rule in theme.css holds an empty
       box at zero opacity for the rest of the session and the panel silently loses its banner. */
    img.addEventListener('error', show, { once: true });
  };

  const start = (img, priority) => {
    if (started.has(img)) return;
    started.add(img);
    if (priority) img.setAttribute('fetchpriority', priority);
    if (img.loading === 'lazy') img.loading = 'eager';
  };

  const startBanners = () => {
    document.querySelectorAll(`${PANELS} ${BANNER}`).forEach((img) => {
      easeIn(img);
      start(img, 'high');
    });
  };

  const startPanel = (id) => {
    if (!id) return;
    const panel = document.getElementById(id);
    if (!panel) return;
    panel.querySelectorAll('img').forEach((img) => start(img));
  };

  const sidebar = document.querySelector(SIDEBAR);
  if (!sidebar) return;

  /* The drawer's own event, dispatched on the element and not bubbling, which is why this listens on the element
     rather than on the document. `before-show` rather than `after-show`: the animation is time this can use. */
  sidebar.addEventListener('dialog:before-show', startBanners);

  /* Capture, so the request is in flight before theme.js unhides the panel and begins its fade — the two happen
     in the same click, and this way the picture is asked for at the start of it rather than the end. */
  sidebar.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('.header-sidebar__main-panel [aria-controls]');
      if (!button) return;
      startBanners();
      startPanel(button.getAttribute('aria-controls'));
    },
    { capture: true }
  );
})();
